<script lang="ts" setup>
import {
    DetailComp,
    ContentComp,
    TabComp,
    InputText,
    InputNullableInt,
    InputSelect,
    InputDropdownCheckboxes,
    UnsavedChangesMessage,
} from '@/components';
import { useEventCommonStore, useGuiStore, useEventStore, useEventCommonStoreRef, useCommonStoreRef } from '@/stores';
import { TabContainer, TabDefinition } from '@/objects/Tabs';
import { Category, CategoryGroupConnection } from '@/objects/generated';
import { BaseObjDao } from '@/dao/BaseObjDao';
import { useTaskLoader } from '@/composeables/loaders';
import { featureIcons } from '@/.';
import { ref } from 'vue';
import type DetailViewExpose from '@/objects/DetailViewExpose';
import type { FieldAlertMessage, SelectLine } from '@/objects';
import { computed } from 'vue';
import { FormLayout, FormLayoutLabel, FormLayoutRow } from '@/components/Form';
import { ValidatorDef } from '@/objects/gui';
import { watch } from 'vue';

// The model for the view
const model = ref<Category | null>(null);

const guiStore = useGuiStore();
const eventStore = useEventStore();
const comStore = useEventCommonStore();
const { Groups } = useEventCommonStoreRef();
const { GendersAsSelectLine } = useCommonStoreRef();

const props = defineProps<{
    /**
     * The id of the category to load. If negative we create a new one
     */
    id: number;

    /**
     * If we need to filter on groups for a specific race
     */
    raceId?: number;
}>();

const detailComp = ref<{ messages: FieldAlertMessage[] } | null>(null);

const changed = computed(() => (model.value?.EntityState ?? 'Unchanged') !== 'Unchanged');

/**
 * Datasource for the dropdown.
 */
const groupsDatasource = computed(() => {
    return (
        Groups.value
            ?.filter((x) => (!props.raceId ? true : x.RaceID == props.raceId))
            .map((x) => {
                return {
                    text: x.Name,
                    value: x.ID,
                } as SelectLine;
            }) || []
    );
});

const selectedGroups = ref<Array<number>>([]);

const { AddLoader, LoadState } = useTaskLoader();
AddLoader(async () => {
    model.value = await BaseObjDao.LoadOrCreateSingle(Category, props.id, eventStore.eventID);

    await comStore.EnsureLoaded(['Groups']);

    // We set some default values on new categories
    if (model.value.EntityState == 'Added') {
        // If we only have one group, we add the connection by default
        if (Groups.value?.length === 1) {
            selectedGroups.value = [Groups.value[0].ID];
        }
    }

    // We iterate the groups connections and update the values
    model.value.CategoryGroupConnection.forEach((c) => {
        const group = Groups.value.find((g) => g.ID == c.GroupID);
        if (group) {
            selectedGroups.value.push(group.ID);
        }
    });

    // We need to check the groups that are in the model
}, 'category.loading_category');

/**
 * We need to maintain the CategoryGroupConnection array based on the selected groups.
 */
watch(selectedGroups, (newValue) => {
    if (!model.value) {
        return;
    }

    // Remove any that is Added, but not currently selected
    // This will actually remove from the collection
    const newCategoryConnections = model.value.CategoryGroupConnection.filter(
        (c) => !(c.EntityState == 'Added' && !newValue.includes(c.GroupID)),
    );

    // Delete the ones that are not in the newvalue anymore
    newCategoryConnections.filter((c) => !newValue.includes(c.GroupID)).forEach((c) => c.Delete());

    // Undelete the ones that have been re-added
    newCategoryConnections.filter((c) => c.EntityState == 'Deleted' && newValue.includes(c.GroupID)).forEach((c) => c.DeleteUndo());

    // Add the ones that are actually new
    newValue
        .filter((groupId) => !newCategoryConnections.some((c) => c.GroupID === groupId))
        .forEach((groupId) => {
            const connection = CategoryGroupConnection.FromJsValues({
                GroupID: groupId,
                EntityState: 'Added',
                EventID: eventStore.eventID,
                CategoryID: model.value!.ID,
            });
            newCategoryConnections.push(connection);
        });

    // Update the actual modelvalue
    model.value.CategoryGroupConnection = newCategoryConnections;
});

/**
 * We help set some values on newly added categories. NOTE: We only set the default values when the category is new, otherwise we might override existing values.
 */
watch(
    () => ({ EntityState: model.value?.EntityState ?? 'Unchanged', AgeTo: model.value?.AgeTo ?? 0 }),
    () => {
        if (!model.value || model.value.EntityState !== 'Added') {
            return;
        }
        model.value.Ranking = (model.value.AgeTo ?? 0) >= 12;
    },
    { deep: true },
);

// Define the tabs
const tabs = new TabContainer();
tabs.AddTab(new TabDefinition('category-general', 'category.general', featureIcons.CATEGORY));

// Expose the view
defineExpose<DetailViewExpose<Category>>({
    val: computed(() => model.value),
    title: computed(() => model.value?.Name ?? ''),
    changed: changed,
    messages: computed(() => (detailComp.value !== null ? detailComp.value!.messages : [])),
});
</script>
<template>
    <ContentComp :state="LoadState">
        <TabComp
            :tabs="tabs"
            :pinable="false"
            :hide-tabs-on-only-one="true"
            v-if="model != null"
        >
            <template #tab-category-general>
                <UnsavedChangesMessage
                    v-if="changed"
                    class="mb-2"
                />

                <!-- First we show the information we HAVE to fill in. i.e Name, Gender, Age from - to, Dropdown for groups -->
                <FormLayout>
                    <FormLayoutRow>
                        <FormLayoutLabel text="category.name"></FormLayoutLabel>
                        <InputText
                            :validator="new ValidatorDef({ Min: null, Max: null, Required: true })"
                            v-model="model!.Name"
                        />
                    </FormLayoutRow>

                    <FormLayoutRow>
                        <FormLayoutLabel text="category.agefromto"></FormLayoutLabel>
                        <div class="d-flex flex-row flex-nowrap gap-2 w-50">
                            <InputNullableInt
                                icon=""
                                v-model="model!.AgeFrom!"
                                :validator="new ValidatorDef({ Min: 0, Max: 200, Required: true })"
                                posttext="general.years"
                            />
                            <span class="px-3">-</span>
                            <InputNullableInt
                                icon=""
                                v-model="model!.AgeTo!"
                                :validator="new ValidatorDef({ Min: 0, Max: 200, Required: true })"
                                posttext="general.years"
                            />
                        </div>
                    </FormLayoutRow>
                    <FormLayoutRow>
                        <FormLayoutLabel text="category.gender"></FormLayoutLabel>
                        <div class="w-50">
                            <InputSelect
                                :datasource="{ mapsource: GendersAsSelectLine }"
                                v-model="model!.Gender"
                            />
                        </div>
                    </FormLayoutRow>

                    <FormLayoutRow>
                        <FormLayoutLabel text="category.groups"></FormLayoutLabel>
                        <InputDropdownCheckboxes
                            class="flex-fill"
                            :data="groupsDatasource"
                            v-model="selectedGroups"
                            :hasSelectAllCheckbox="true"
                        >
                            {{ $tm('category.selectgroups') }}
                        </InputDropdownCheckboxes>
                    </FormLayoutRow>
                </FormLayout>
                <!-- We remove the mandatory fields from the GUIDef and use the detail comp for the rest. NOTE: We need to group and collapse the groups -->
                <DetailComp
                    :gui="guiStore.Category"
                    v-model="model"
                    ref="detailComp"
                />
            </template>
        </TabComp>
    </ContentComp>
</template>
